import {
	AddEquation,
	Color,
	CustomBlending,
	DataTexture,
	DstAlphaFactor,
	DstColorFactor,
	HalfFloatType,
	NoBlending,
	RepeatWrapping,
	RGBAFormat,
	ShaderMaterial,
	UniformsUtils,
	UnsignedByteType,
	WebGLRenderTarget,
	ZeroFactor,
} from 'three';

import {generateMagicSquareNoise, GTAOShader, GTAOBlendShader } from 'three/examples/jsm/shaders/GTAOShader';
import {PoissonDenoiseShader, generatePdSamplePointInitializer} from 'three/examples/jsm/shaders/PoissonDenoiseShader';
import {CopyShader} from 'three/examples/jsm/shaders/CopyShader';
import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass';
import {SimplexNoise} from 'three/examples/jsm/math/SimplexNoise';
import { Effect } from './Effect';

class LiteGTAO extends Pass {
	
	constructor(camera, width, height) {
		
		super();
		
		this.width = ( width !== undefined ) ? width : 512;
		this.height = ( height !== undefined ) ? height : 512;
		this.camera = camera;
		this.blendIntensity = 1.;
		this.output = 0;
		
		this.pdRings = 2.;
		this.pdRadiusExponent = 2.;
		this.pdSamples = 16;
		
		this.gtaoNoiseTexture = generateMagicSquareNoise();
		this.pdNoiseTexture = this.generateNoise();
		
		this.gtaoRenderTarget = new WebGLRenderTarget( this.width, this.height, { type: HalfFloatType } );
		this.pdRenderTarget = this.gtaoRenderTarget.clone();
		
		this.gtaoMaterial = new ShaderMaterial( {
			defines: Object.assign( {}, GTAOShader.defines ),
			uniforms: UniformsUtils.clone( GTAOShader.uniforms ),
			vertexShader: GTAOShader.vertexShader,
			fragmentShader: GTAOShader.fragmentShader,
			blending: NoBlending,
			depthTest: false,
			depthWrite: false,
		} );
		this.gtaoMaterial.defines.PERSPECTIVE_CAMERA = this.camera.isPerspectiveCamera ? 1 : 0;
		this.gtaoMaterial.uniforms.tNoise.value = this.gtaoNoiseTexture;
		this.gtaoMaterial.uniforms.resolution.value.set( this.width, this.height );
		this.gtaoMaterial.uniforms.cameraNear.value = this.camera.near;
		this.gtaoMaterial.uniforms.cameraFar.value = this.camera.far;
		
		this.pdMaterial = new ShaderMaterial( {
			defines: Object.assign( {}, PoissonDenoiseShader.defines ),
			uniforms: UniformsUtils.clone( PoissonDenoiseShader.uniforms ),
			vertexShader: PoissonDenoiseShader.vertexShader,
			fragmentShader: PoissonDenoiseShader.fragmentShader,
			depthTest: false,
			depthWrite: false,
		} );
		this.pdMaterial.uniforms.tDiffuse.value = this.gtaoRenderTarget.texture;
		this.pdMaterial.uniforms.tNoise.value = this.pdNoiseTexture;
		this.pdMaterial.uniforms.resolution.value.set( this.width, this.height );
		this.pdMaterial.uniforms.lumaPhi.value = 10;
		this.pdMaterial.uniforms.depthPhi.value = 2;
		this.pdMaterial.uniforms.normalPhi.value = 3;
		this.pdMaterial.uniforms.radius.value = 8;
		
		this.copyMaterial = new ShaderMaterial( {
			uniforms: UniformsUtils.clone( CopyShader.uniforms ),
			vertexShader: CopyShader.vertexShader,
			fragmentShader: CopyShader.fragmentShader,
			transparent: true,
			depthTest: false,
			depthWrite: false,
			blendSrc: DstColorFactor,
			blendDst: ZeroFactor,
			blendEquation: AddEquation,
			blendSrcAlpha: DstAlphaFactor,
			blendDstAlpha: ZeroFactor,
			blendEquationAlpha: AddEquation
		} );
		
		this.blendMaterial = new ShaderMaterial( {
			uniforms: UniformsUtils.clone( GTAOBlendShader.uniforms ),
			vertexShader: GTAOBlendShader.vertexShader,
			fragmentShader: GTAOBlendShader.fragmentShader,
			transparent: true,
			depthTest: false,
			depthWrite: false,
			blending: CustomBlending,
			blendSrc: DstColorFactor,
			blendDst: ZeroFactor,
			blendEquation: AddEquation,
			blendSrcAlpha: DstAlphaFactor,
			blendDstAlpha: ZeroFactor,
			blendEquationAlpha: AddEquation
		} );
		
		this.fsQuad = new FullScreenQuad( null );
		
		this.originalClearColor = new Color();
		
		this.gtaoMaterial.defines.NORMAL_VECTOR_TYPE = 0;
		this.gtaoMaterial.defines.DEPTH_SWIZZLING = 'x';
		
		this.pdMaterial.defines.NORMAL_VECTOR_TYPE = 0;
		this.pdMaterial.defines.DEPTH_SWIZZLING = 'x';
		
		this.needsSwap = false;
	}
	
	dispose() {
		this.gtaoNoiseTexture.dispose();
		this.pdNoiseTexture.dispose();
		this.gtaoRenderTarget.dispose();
		this.pdRenderTarget.dispose();
		this.pdMaterial.dispose();
		this.copyMaterial.dispose();
		this.fsQuad.dispose();
	}
	
	get gtaoMap() {
		
		return this.pdRenderTarget.texture;
		
	}
	
	setSceneClipBox( box ) {
		
		if ( box ) {
			
			this.gtaoMaterial.needsUpdate = this.gtaoMaterial.defines.SCENE_CLIP_BOX !== 1;
			this.gtaoMaterial.defines.SCENE_CLIP_BOX = 1;
			this.gtaoMaterial.uniforms.sceneBoxMin.value.copy( box.min );
			this.gtaoMaterial.uniforms.sceneBoxMax.value.copy( box.max );
			
		} else {
			
			this.gtaoMaterial.needsUpdate = this.gtaoMaterial.defines.SCENE_CLIP_BOX === 0;
			this.gtaoMaterial.defines.SCENE_CLIP_BOX = 0;
			
		}
		
	}
	
	updateGtaoMaterial( parameters ) {
		
		if ( parameters.radius !== undefined ) {
			
			this.gtaoMaterial.uniforms.radius.value = parameters.radius;
			
		}
		
		if ( parameters.distanceExponent !== undefined ) {
			
			this.gtaoMaterial.uniforms.distanceExponent.value = parameters.distanceExponent;
			
		}
		
		if ( parameters.thickness !== undefined ) {
			
			this.gtaoMaterial.uniforms.thickness.value = parameters.thickness;
			
		}
		
		if ( parameters.distanceFallOff !== undefined ) {
			
			this.gtaoMaterial.uniforms.distanceFallOff.value = parameters.distanceFallOff;
			this.gtaoMaterial.needsUpdate = true;
			
		}
		
		if ( parameters.scale !== undefined ) {
			
			this.gtaoMaterial.uniforms.scale.value = parameters.scale;
			
		}
		
		if ( parameters.samples !== undefined && parameters.samples !== this.gtaoMaterial.defines.SAMPLES ) {
			
			this.gtaoMaterial.defines.SAMPLES = parameters.samples;
			this.gtaoMaterial.needsUpdate = true;
			
		}
		
		if ( parameters.screenSpaceRadius !== undefined && ( parameters.screenSpaceRadius ? 1 : 0 ) !== this.gtaoMaterial.defines.SCREEN_SPACE_RADIUS ) {
			
			this.gtaoMaterial.defines.SCREEN_SPACE_RADIUS = parameters.screenSpaceRadius ? 1 : 0;
			this.gtaoMaterial.needsUpdate = true;
			
		}
		
	}
	
	updatePdMaterial( parameters ) {
		
		let updateShader = false;
		
		if ( parameters.lumaPhi !== undefined ) {
			
			this.pdMaterial.uniforms.lumaPhi.value = parameters.lumaPhi;
			
		}
		
		if ( parameters.depthPhi !== undefined ) {
			
			this.pdMaterial.uniforms.depthPhi.value = parameters.depthPhi;
			
		}
		
		if ( parameters.normalPhi !== undefined ) {
			
			this.pdMaterial.uniforms.normalPhi.value = parameters.normalPhi;
			
		}
		
		if ( parameters.radius !== undefined && parameters.radius !== this.radius ) {
			
			this.pdMaterial.uniforms.radius.value = parameters.radius;
			
		}
		
		if ( parameters.radiusExponent !== undefined && parameters.radiusExponent !== this.pdRadiusExponent ) {
			
			this.pdRadiusExponent = parameters.radiusExponent;
			updateShader = true;
			
		}
		
		if ( parameters.rings !== undefined && parameters.rings !== this.pdRings ) {
			
			this.pdRings = parameters.rings;
			updateShader = true;
			
		}
		
		if ( parameters.samples !== undefined && parameters.samples !== this.pdSamples ) {
			
			this.pdSamples = parameters.samples;
			updateShader = true;
			
		}
		
		if ( updateShader ) {
			
			this.pdMaterial.defines.SAMPLES = this.pdSamples;
			this.pdMaterial.defines.SAMPLE_VECTORS = generatePdSamplePointInitializer( this.pdSamples, this.pdRings, this.pdRadiusExponent );
			this.pdMaterial.needsUpdate = true;
			
		}
		
	}
	
	render( renderer, writeBuffer, readBuffer /*, deltaTime, maskActive */ ) {
		// render AO
		this.gtaoMaterial.uniforms.cameraNear.value = this.camera.near;
		this.gtaoMaterial.uniforms.cameraFar.value = this.camera.far;
		this.gtaoMaterial.uniforms.tDepth.value = readBuffer.depthTexture;
		this.gtaoMaterial.uniforms.cameraProjectionMatrix.value.copy( this.camera.projectionMatrix );
		this.gtaoMaterial.uniforms.cameraProjectionMatrixInverse.value.copy( this.camera.projectionMatrixInverse );
		this.gtaoMaterial.uniforms.cameraWorldMatrix.value.copy( this.camera.matrixWorld );
		this.renderPass( renderer, this.gtaoMaterial, this.gtaoRenderTarget, 0xffffff, 1.0 );
		//
		// // render poisson denoise
		this.pdMaterial.uniforms.cameraProjectionMatrixInverse.value.copy( this.camera.projectionMatrixInverse );
		this.pdMaterial.uniforms.tDepth.value = readBuffer.depthTexture;
		this.renderPass( renderer, this.pdMaterial, this.pdRenderTarget, 0xffffff, 1.0 );
		
		switch ( this.output ) {
			
			case 0:
				this.copyMaterial.uniforms.tDiffuse.value = readBuffer.texture;
				this.copyMaterial.blending = NoBlending;
				this.renderPass( renderer, this.copyMaterial, this.renderToScreen ? null : readBuffer );
				
				this.blendMaterial.uniforms.intensity.value = this.blendIntensity;
				this.blendMaterial.uniforms.tDiffuse.value = this.pdRenderTarget.texture;
				this.renderPass( renderer, this.blendMaterial, this.renderToScreen ? null : readBuffer );
				
				break;
				
			case 1:
				this.copyMaterial.uniforms.tDiffuse.value = readBuffer.texture;
				this.copyMaterial.blending = NoBlending;
				this.renderPass( renderer, this.copyMaterial, this.renderToScreen ? null : readBuffer );
				break;
				
			case 2:
				this.copyMaterial.uniforms.tDiffuse.value = this.gtaoRenderTarget.texture;
				this.copyMaterial.blending = NoBlending;
				this.renderPass( renderer, this.copyMaterial, this.renderToScreen ? null : readBuffer );
				break;
				
			case 3:
				this.copyMaterial.uniforms.tDiffuse.value = this.pdRenderTarget.texture;
				this.copyMaterial.blending = NoBlending;
				this.renderPass( renderer, this.copyMaterial, this.renderToScreen ? null : readBuffer );
				break;
		}
	}
	
	renderPass( renderer, passMaterial, renderTarget, clearColor, clearAlpha ) {
		
		// save original state
		renderer.getClearColor( this.originalClearColor );
		const originalClearAlpha = renderer.getClearAlpha();
		const originalAutoClear = renderer.autoClear;
		
		renderer.setRenderTarget( renderTarget );
		
		// setup pass state
		renderer.autoClear = false;
		if ( ( clearColor !== undefined ) && ( clearColor !== null ) ) {
			
			renderer.setClearColor( clearColor );
			renderer.setClearAlpha( clearAlpha || 0.0 );
			renderer.clear();
			
		}
		
		this.fsQuad.material = passMaterial;
		this.fsQuad.render( renderer );
		
		// restore original state
		renderer.autoClear = originalAutoClear;
		renderer.setClearColor( this.originalClearColor );
		renderer.setClearAlpha( originalClearAlpha );
		
	}
	
	setSize( width, height ) {
		
		this.width = width;
		this.height = height;
		
		this.gtaoRenderTarget.setSize( width, height );
		this.pdRenderTarget.setSize( width, height );
		
		this.gtaoMaterial.uniforms.resolution.value.set( width, height );
		this.gtaoMaterial.uniforms.cameraProjectionMatrix.value.copy( this.camera.projectionMatrix );
		this.gtaoMaterial.uniforms.cameraProjectionMatrixInverse.value.copy( this.camera.projectionMatrixInverse );
		
		this.pdMaterial.uniforms.resolution.value.set( width, height );
		this.pdMaterial.uniforms.cameraProjectionMatrixInverse.value.copy( this.camera.projectionMatrixInverse );
		
	}
	
	generateNoise( size = 64 ) {
		
		const simplex = new SimplexNoise();
		
		const arraySize = size * size * 4;
		const data = new Uint8Array( arraySize );
		
		for ( let i = 0; i < size; i ++ ) {
			
			for ( let j = 0; j < size; j ++ ) {
				
				const x = i;
				const y = j;
				
				data[ ( i * size + j ) * 4 ] = ( simplex.noise( x, y ) * 0.5 + 0.5 ) * 255;
				data[ ( i * size + j ) * 4 + 1 ] = ( simplex.noise( x + size, y ) * 0.5 + 0.5 ) * 255;
				data[ ( i * size + j ) * 4 + 2 ] = ( simplex.noise( x, y + size ) * 0.5 + 0.5 ) * 255;
				data[ ( i * size + j ) * 4 + 3 ] = ( simplex.noise( x + size, y + size ) * 0.5 + 0.5 ) * 255;
				
			}
			
		}
		
		const noiseTexture = new DataTexture( data, size, size, RGBAFormat, UnsignedByteType );
		noiseTexture.wrapS = RepeatWrapping;
		noiseTexture.wrapT = RepeatWrapping;
		noiseTexture.needsUpdate = true;
		
		return noiseTexture;
		
	}
	
}

export class GTAOLite extends Effect{
	constructor (camera) {
		super();
		
		this.effect = new LiteGTAO(camera,window.innerWidth,window.innerHeight);
		
		this.aoParameters = {
			radius: 0.25,
			distanceExponent: 1.,
			thickness: 1.,
			scale: 1.,
			samples: 16,
			distanceFallOff: 1.,
			screenSpaceRadius: true,
		};
		this.pdParameters = {
			lumaPhi: 10.,
			depthPhi: 2.,
			normalPhi: 3.,
			radius: 4.,
			radiusExponent: 1.,
			rings: 2.,
			samples: 16,
		};
		
		this.effect.updateGtaoMaterial(this.aoParameters);
		this.effect.updatePdMaterial(this.pdParameters);
		this.active = false;
	}
	
	get config() {
		return {
			enabled: this.active,
			aoRadius: this.aoParameters.radius,
			distanceExponent: this.aoParameters.distanceExponent,
			thickness: this.aoParameters.thickness,
			scale: this.aoParameters.scale,
			aoSamples: this.aoParameters.samples,
			distanceFallOff: this.aoParameters.distanceFallOff,
			screenSpaceRadius: this.aoParameters.screenSpaceRadius,
			
			lumaPhi: this.pdParameters.lumaPhi,
			depthPhi: this.pdParameters.depthPhi,
			normalPhi: this.pdParameters.normalPhi,
			pdRadius: this.pdParameters.radius,
			radiusExponent: this.pdParameters.radiusExponent,
			rings: this.pdParameters.rings,
			pdSamples: this.pdParameters.samples,
		}
	}
	
	set config(conf) {
		this.aoParameters = {
			radius: conf.aoRadius,
			distanceExponent: conf.distanceExponent,
			thickness: conf.thickness,
			scale: conf.scale,
			samples: conf.aoSamples,
			distanceFallOff: conf.distanceFallOff,
			screenSpaceRadius: conf.screenSpaceRadius
		};
		
		this.pdParameters = {
			lumaPhi: conf.lumaPhi,
			depthPhi: conf.depthPhi,
			normalPhi: conf.normalPhi,
			radius: conf.pdRadius,
			radiusExponent: conf.radiusExponent,
			rings: conf.rings,
			samples: conf.pdSamples
		};
		
		this.effect.updateGtaoMaterial(this.aoParameters);
		this.effect.updatePdMaterial(this.pdParameters);
		this.active = conf.enabled;
	}
}

